import {
  and,
  asc,
  eq,
  gte,
  lte,
  meal_plan_entry,
  type PowerSyncDrizzleDB,
  recipe,
} from "@mealplan/powersync";
import { liveRecipeFilters } from "../live/filters";

export const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"] as const;
export type MealType = (typeof MEAL_TYPES)[number];

const DAYS_PER_WEEK = 7;
const ISO_DATE_LENGTH = 10;

/** Local `YYYY-MM-DD` (without timezone) for a Date. */
export function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Monday of the week containing `date` (European week). */
export function startOfWeek(date: Date): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const weekday = (result.getDay() + 6) % DAYS_PER_WEEK; // 0 = lunes
  result.setDate(result.getDate() - weekday);
  return result;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** The 7 dates (`YYYY-MM-DD`) of the week starting at weekStart. */
export function weekDateKeys(weekStart: Date): string[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_, i) => toDateKey(addDays(weekStart, i)));
}

export function isDateKey(value: string): boolean {
  return value.length === ISO_DATE_LENGTH && /^\d{4}-\d{2}-\d{2}$/u.test(value);
}

/** Plan entries in [startDate, endDate] with their recipe data. */
export function getMealPlanRangeQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  startDate: string,
  endDate: string,
) {
  return (
    db
      .select({
        id: meal_plan_entry.id,
        plan_date: meal_plan_entry.plan_date,
        meal_type: meal_plan_entry.meal_type,
        recipe_id: meal_plan_entry.recipe_id,
        servings: meal_plan_entry.servings,
        note: meal_plan_entry.note,
        // 🔖 Pointer/seal stamp (00050): version with which the
        // dish was planned + pointer to the LIVE recipe version. The "recipe
        // modified since it was planned" badge is a pure id comparison
        // (see recipeChangedSincePlanned).
        recipe_version_id: meal_plan_entry.recipe_version_id,
        recipe_current_version_id: recipe.current_version_id,
        recipe_name: recipe.name,
        recipe_servings: recipe.servings,
        // 🥗 Nutrition PER SERVING of the recipe (00052): the planner scales it
        // by the planned servings (see nutrition/nutrition.ts).
        recipe_calories: recipe.calories,
        recipe_protein_g: recipe.protein_g,
        recipe_carbs_g: recipe.carbs_g,
        recipe_fat_g: recipe.fat_g,
      })
      .from(meal_plan_entry)
      // The join excludes recipes in the trash: a slot referencing a recipe
      // that was deleted remains UNRESOLVED (recipe_name = null) and resolves again when
      // it is restored from the trash.
      .leftJoin(recipe, and(eq(meal_plan_entry.recipe_id, recipe.id), ...liveRecipeFilters()))
      .where(
        and(
          eq(meal_plan_entry.user_id, userId),
          gte(meal_plan_entry.plan_date, startDate),
          lte(meal_plan_entry.plan_date, endDate),
        ),
      )
      .orderBy(asc(meal_plan_entry.plan_date), asc(meal_plan_entry.created_at))
  );
}

export type MealPlanEntryWithRecipe = Awaited<
  ReturnType<ReturnType<typeof getMealPlanRangeQuery>["execute"]>
>[number];

/**
 * Has the recipe changed since this dish was planned? (planner badge).
 *
 * Pure id comparison thanks to the "current-becomes-history" unification:
 * when planning, the dish seals `recipe_version_id` with the current version of the
 * recipe (== `current_version_id`). When the recipe is EDITED, that current row
 * becomes history and `recipe.current_version_id` becomes NULL (or, if another
 * dish is sealed again, a different version). Thus:
 *
 *   sealed (recipe_version_id != NULL) AND differs from the current live version
 *   (current_version_id, which may be NULL) ⇒ recipe modified.
 *
 * Legacy dishes without a seal (recipe_version_id == NULL) never show the badge.
 */
export function recipeChangedSincePlanned(entry: {
  recipe_version_id: string | null;
  recipe_current_version_id: string | null;
}): boolean {
  return (
    entry.recipe_version_id != null && entry.recipe_version_id !== entry.recipe_current_version_id
  );
}

/**
 * Plan entries (with date and meal type) that use a specific recipe.
 * Interconnection: recipe → where it is planned (Phase 5, axis 6).
 */
export function getMealPlanByRecipeQuery(db: PowerSyncDrizzleDB, userId: string, recipeId: string) {
  return db
    .select({
      id: meal_plan_entry.id,
      plan_date: meal_plan_entry.plan_date,
      meal_type: meal_plan_entry.meal_type,
      servings: meal_plan_entry.servings,
    })
    .from(meal_plan_entry)
    .where(and(eq(meal_plan_entry.user_id, userId), eq(meal_plan_entry.recipe_id, recipeId)))
    .orderBy(asc(meal_plan_entry.plan_date));
}

export type MealPlanByRecipeRow = Awaited<
  ReturnType<ReturnType<typeof getMealPlanByRecipeQuery>["execute"]>
>[number];
